import * as React from 'react';

// Type-only import so the `{@link AlertDialog}` JSDoc reference below resolves.
// eslint-disable-next-line unused-imports/no-unused-imports, @typescript-eslint/no-unused-vars
import { type AlertDialog } from './AlertDialog';
import { VariantProps, cn, tv } from './utils/cn';

const alertVariants = tv({
  base: "group/alert relative grid w-full gap-0.5 rounded-lg border px-2.5 py-2 text-left text-sm has-[>svg]:grid-cols-[auto_1fr] has-[>svg]:gap-x-2 *:[svg]:row-span-2 *:[svg]:translate-y-0.5 *:[svg]:text-current *:[svg:not([class*='size-'])]:size-4",
  variants: {
    variant: {
      default: 'bg-card text-card-foreground',
      destructive:
        'bg-card text-destructive *:data-[slot=alert-description]:text-destructive/90 *:[svg]:text-current',
      warning:
        'border-border-warning-subtle bg-bg-warning-subtle text-text-warning-subtle *:[svg]:text-text-warning-subtle',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

type AlertVariant = NonNullable<VariantProps<typeof alertVariants>['variant']>;

/**
 * Inline notice that surfaces a status, warning, or error inline with page
 * content. Compose with {@link AlertTitle} and {@link AlertDescription}; pass
 * an `<Icon>` as the first child for a leading visual.
 *
 * For a blocking modal that demands a yes/no decision, use
 * {@link AlertDialog} instead.
 *
 * @example
 * <Alert variant="warning">
 *   <Icon name="AlertTriangle" />
 *   <AlertTitle>Unsigned note</AlertTitle>
 *   <AlertDescription>
 *     Sign before the next visit so it appears in the patient's chart.
 *   </AlertDescription>
 * </Alert>
 */
function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  );
}

/** Headline of an {@link Alert}. Keep it short; the action belongs in the description. */
function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        'font-medium group-has-[>svg]/alert:col-start-2 [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground',
        className,
      )}
      {...props}
    />
  );
}

/** Body text of an {@link Alert}. Holds the explanation and any inline links. */
function AlertDescription({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        'text-sm text-balance text-muted-foreground md:text-pretty [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground [&_p:not(:last-child)]:mb-4',
        className,
      )}
      {...props}
    />
  );
}

export { Alert, AlertTitle, AlertDescription, type AlertVariant };
